import React, { useState, useEffect } from "react";
import { Trash2, Edit2, Check, ChevronLeft, ChevronRight } from "lucide-react";
import "./../studentComponents/booksContentPage.css";

const BookListingPage = () => {
  const [listings, setListings] = useState([]);
  const [newListing, setNewListing] = useState({
    book_name: "",
    book_price: "",
    book_price_after_discount: "",
    book_category: "",
    book_description: "",
    product_id: "",
    book_image: null,
    book_document: null,
    book_type: "",
  });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await fetch(
        "https://admindashboard.clns.in/api/student/book_category_create/"
      );
      const data = await response.json();
      if (data.status) {
        setCategories(data.categories);
      } else {
        setError("Failed to load categories");
      }
    } catch (error) {
      setError("Error fetching categories");
    }
  };

  // Fetch listings from API with pagination
  const fetchListings = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `https://admindashboard.clns.in/api/student/books_list/?page=${page}`
      );
      const data = await response.json();
      if (data.status) {
        setListings(data.books);
        setTotalPages(2);
      } else {
        setError("Failed to load listings");
      }
    } catch (error) {
      setError("Error fetching listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings(currentPage); // Fetch listings for the current page
    fetchCategories(); // Fetch categories on component mount
  }, [currentPage]); // Re-fetch when page changes

  // Handle page navigation
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle input change for the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewListing((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle category input change
  const handleCategoryChange = (e) => {
    setNewCategory(e.target.value);
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewListing((prev) => ({
        ...prev,
        book_image: file,
      }));
      setSelectedImage(URL.createObjectURL(file)); // Set the image preview immediately
    }
  };

  // Handle document upload
  const handleDocumentUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewListing((prev) => ({
        ...prev,
        book_document: file,
      }));
    }
  };

  // Handle book type change
  const handleBookTypeChange = (e) => {
    const { value } = e.target;
    setNewListing((prev) => ({
      ...prev,
      book_type: value,
      // Clear book_document if hands_on is selected
      book_document: value === 'hands_on' ? null : prev.book_document, 
    }));
  };

  // Handle form submission to POST new listing
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Basic validation
    if (
      !newListing.book_name ||
      !newListing.book_price ||
      !newListing.product_id ||
      !newListing.book_type
    ) {
      setError("Book name, price, product ID, and book type are required");
      setLoading(false);
      return;
    }

    // Validate that e-books must have a document
    if (newListing.book_type === "e_book" && !newListing.book_document) {
      setError("Document is required for e-books");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("book_name", newListing.book_name);
      formData.append("book_price", newListing.book_price);
      formData.append(
        "book_price_after_discount",
        newListing.book_price_after_discount
      );
      formData.append("book_category", newListing.book_category);
      formData.append("book_description", newListing.book_description);
      formData.append("product_id", newListing.product_id);
      formData.append("book_type", newListing.book_type);
      
      if (newListing.book_image) {
        formData.append("book_image", newListing.book_image);
      }
      
      // Only append book_document if not a hands_on book
      if (newListing.book_type === "e_book" && newListing.book_document) {
        formData.append("book_document", newListing.book_document);
      }

      const response = await fetch(
        "https://admindashboard.clns.in/api/student/books/create/",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.status) {
        fetchListings(currentPage); // Fetch the updated listings for current page
        setNewListing({
          book_name: "",
          book_price: "",
          book_price_after_discount: "",
          book_category: "",
          book_description: "",
          product_id: "",
          book_image: null,
          book_document: null,
          book_type: "", 
        });
        setSelectedImage(null); // Reset image preview after submit
      } else {
        setError(data.message || "Failed to add new listing");
      }
    } catch (error) {
      setError("Error adding listing");
    } finally {
      setLoading(false);
    }
  };

  // Handle category form submission to POST new category
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!newCategory) {
      setError("Category name is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("bookCategorys", newCategory);

      const response = await fetch(
        "https://admindashboard.clns.in/api/student/book_category_create/",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.status) {
        setNewCategory(""); // Clear the input field
        fetchCategories(); // Fetch the updated category list
        fetchListings(currentPage); // Refresh the listings after the category is added
      } else {
        setError("Failed to add new category");
      }
    } catch (error) {
      setError("Error adding category");
    } finally {
      setLoading(false);
    }
  };

  // Handle deletion of listing
  const handleDelete = async (productId) => {
    console.log(productId, "productId");
    setLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError("Authentication token not found. Please log in again.");
        setLoading(false);
        return;
      }
      
      const response = await fetch(
        `https://admindashboard.clns.in/api/student/books/delete/${productId}/`,
        {
          method: "DELETE",
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log(productId)
      const data = await response.json();
      console.log(data)
      if (data.status) {
        fetchListings(currentPage); // Refresh listings after successful deletion
      } else {
        setError(data.message || "Failed to delete listing");
      }
    } catch (error) {
      console.error("Error during deletion:", error);
      setError("Error deleting listing");
    } finally {
      setLoading(false);
    }
  };

  // Handle editing of listing
  const handleEdit = (listing) => {
    setNewListing({
      book_name: listing.book_name,
      book_price: listing.book_price,
      book_price_after_discount: listing.book_price_after_discount,
      book_category: listing.book_category,
      book_description: listing.book_description,
      product_id: listing.product_id,
      book_image: null,
      book_document: null,
      book_type: listing.book_type,
    });
  };

  // Optionally clear the image preview
  const handleClearImagePreview = () => {
    setSelectedImage(null);
    setNewListing((prev) => ({
      ...prev,
      book_image: null,
    }));
  };

  // Check if the book type is hands_on
  const isHandsOnBook = newListing.book_type === "hands_on";
  // Check if the book type is e-book
  const isEBook = newListing.book_type === "e_book";

  return (
    <div className="container">
      {/* Add New Category Form */}
      <div className="card">
        <div className="card-header">
          <h2>Add New Category</h2>
        </div>
        <div className="card-content">
          <form onSubmit={handleCategorySubmit}>
            <input
              type="text"
              value={newCategory}
              onChange={handleCategoryChange}
              placeholder="Category Name"
              className="input-field"
            />
            <button type="submit" className="button-primary">
              {loading ? "Adding..." : "Add Category"}
            </button>
          </form>
        </div>
      </div>

      {/* Add New Listing Form */}
      <div className="card">
        <div className="card-header">
          <h2>Add New Listing</h2>
        </div>
        <div className="card-content">
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleSubmit} className="form-grid">
            <div className="form-left">
              <input
                type="text"
                placeholder="Product ID"
                name="product_id"
                value={newListing.product_id}
                onChange={handleInputChange}
                className="input-field"
              />
              <input
                type="text"
                placeholder="Book Name"
                name="book_name"
                value={newListing.book_name}
                onChange={handleInputChange}
                className="input-field"
              />
              <div className="price-group">
                <input
                  type="number"
                  placeholder="Price"
                  name="book_price"
                  value={newListing.book_price}
                  onChange={handleInputChange}
                  className="input-field"
                />
                <input
                  type="number"
                  placeholder="After Discount"
                  name="book_price_after_discount"
                  value={newListing.book_price_after_discount}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>
              <textarea
                placeholder="Add Description"
                name="book_description"
                value={newListing.book_description}
                onChange={handleInputChange}
                className="textarea-field"
              />
            </div>
            <div className="form-right">
              <div className="upload-section">
                <label className="upload-label">
                  <input
                    type="file"
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="file-input"
                  />
                  Upload Image
                </label>
                
                {/* Book Type Dropdown - Positioned before document upload for better UX */}
                <select
                  name="book_type"
                  value={newListing.book_type}
                  onChange={handleBookTypeChange}
                  className="select-field"
                >
                  <option value="">Select Book Type</option>
                  <option value="e_book">E-Book</option>
                  <option value="hands_on">Hands-On</option>
                </select>
                
                {/* Document Upload - Disabled for hands_on books */}
                <div className={isHandsOnBook ? "file-input-container disabled" : "file-input-container"}>
                  <label className="upload-label">
                    <input
                      type="file"
                      onChange={handleDocumentUpload}
                      accept=".pdf,.doc,.docx"
                      className="file-input"
                      disabled={isHandsOnBook}
                    />
                    {isEBook ? "Upload Document (Required)" : "Upload Document"}
                  </label>
                  {isHandsOnBook && (
                    <div className="disabled-message">Document upload not needed for Hands-on books</div>
                  )}
                </div>
                
                <select
                  name="book_category"
                  value={newListing.book_category}
                  onChange={handleInputChange}
                  className="select-field"
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.bookCategorys}>
                      {category.bookCategorys}
                    </option>
                  ))}
                </select>
              </div>

              {/* Image Preview Section */}
              {selectedImage && (
                <div className="image-preview">
                  <img src={selectedImage} alt="Preview" />
                  <button
                    type="button"
                    onClick={handleClearImagePreview}
                    className="clear-button"
                  >
                    Clear Image
                  </button>
                </div>
              )}
              <button type="submit" className="button-primary">
                {loading ? "Adding..." : "Add Listing"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Listings Table */}
      <div className="card">
        <div className="card-header">
          <h2>Book Listings</h2>
        </div>
        <div className="card-content">
          {loading ? (
            <p>Loading...</p>
          ) : error ? (
            <p>{error}</p>
          ) : (
            <>
              <table className="listing-table">
                <thead>
                  <tr>
                    <th>Book Name</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((listing) => (
                    <tr key={listing.id || listing.book_id}>
                      <td>{listing.book_name}</td>
                      <td>{listing.book_category}</td>
                      <td>{listing.book_type === "hands_on" ? "Hands-On" : "E-Book"}</td>
                      <td>{listing.book_price}</td>
                      <td>
                        <span 
                          className={listing.is_deleted ? "status-deleted" : "status-active"}
                        >
                          {listing.is_deleted ? "Deleted" : "Active"}
                        </span>
                      </td>
                      <td>
                        {/* <button
                          onClick={() => handleEdit(listing)}
                          className="action-button"
                        >
                          <Edit2 />
                        </button> */}
                        {/* Only show delete button if is_deleted is not true */}
                        {!listing.is_deleted && (
                          <button
                            onClick={() => handleDelete(listing.product_id)}
                            className="action-button"
                          >
                            <Trash2 />
                          </button>
                        )}
                        {listing.book_document && (
                          <a
                            href={listing.book_document}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <button className="action-button">
                              <Check />
                            </button>
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination Controls */}
              <div className="pagination-controls">
                <button 
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  <ChevronLeft />
                  Previous
                </button>
                <span className="page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Next
                  <ChevronRight />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookListingPage;